/**
 * exp/event-registration — form popup modal (shared contact-modal behaviour).
 * Mounts the HubSpot embed only while the modal is visible (re-injected with Cookiebot / WP Rocket ignore).
 */

const STRL_HS_MIN_HEIGHT = 560;

/**
 * @param {HTMLScriptElement} oldScript
 * @returns {string}
 */
function strlGetEventRegistrationScriptSrc( oldScript ) {
	return (
		oldScript.getAttribute( 'data-cookieblock-src' ) ||
		oldScript.getAttribute( 'data-rocket-src' ) ||
		oldScript.getAttribute( 'src' ) ||
		''
	);
}

/**
 * Clone a script so the browser executes it (template clones / Cookiebot / WP Rocket stubs do not).
 *
 * @param {HTMLScriptElement} oldScript
 * @returns {HTMLScriptElement}
 */
function strlCloneExecutableEventRegistrationScript( oldScript ) {
	const newScript = document.createElement( 'script' );

	/**
	 * True when a script type is a consent/optimizer stub that browsers will not execute as JS.
	 *
	 * @param {string} type
	 * @returns {boolean}
	 */
	const isNonExecutableType = ( type ) => {
		if ( ! type ) {
			return false;
		}

		const normalized = String( type ).toLowerCase();

		return (
			-1 !== normalized.indexOf( 'plain' ) ||
			-1 !== normalized.indexOf( 'rocketlazyloadscript' ) ||
			-1 !== normalized.indexOf( 'text/template' )
		);
	};

	Array.from( oldScript.attributes ).forEach( ( attr ) => {
		if ( 'type' === attr.name && isNonExecutableType( attr.value ) ) {
			return;
		}

		if ( 'src' === attr.name ) {
			return;
		}

		// Drop Cookiebot / WP Rocket delay attributes; we re-inject as a real executable script.
		if ( 0 === attr.name.indexOf( 'data-cookie' ) || 0 === attr.name.indexOf( 'data-rocket' ) ) {
			return;
		}

		newScript.setAttribute( attr.name, attr.value );
	} );

	// Bypass Cookiebot auto-blocking on re-inject so registration works without marketing consent.
	newScript.setAttribute( 'data-cookieconsent', 'ignore' );

	const src = strlGetEventRegistrationScriptSrc( oldScript );
	if ( src ) {
		newScript.src = src;
	} else {
		newScript.textContent = oldScript.textContent;
	}

	return newScript;
}

/**
 * Ensure HubSpot iframes stay visible even when HubSpot leaves visibility:hidden / height:0.
 *
 * @param {ParentNode} root
 */
function strlEnsureHubspotFormStyles( root ) {
	if ( ! root || root.querySelector( '[data-strl-hs-style]' ) ) {
		return;
	}

	const style = document.createElement( 'style' );
	style.setAttribute( 'data-strl-hs-style', '' );
	style.textContent = [
		'[data-event-registration-script-content] .hbspt-form,',
		'[data-event-registration-script-content] .hs-form-frame,',
		'[data-event-registration-script-content] iframe,',
		'[data-event-registration-script-content] .hs-form-iframe {',
		'  visibility: visible !important;',
		'  opacity: 1 !important;',
		'  display: block !important;',
		'  width: 100% !important;',
		'  max-width: 100% !important;',
		'  min-height: ' + STRL_HS_MIN_HEIGHT + 'px !important;',
		'}',
	].join( '\n' );

	root.appendChild( style );
}

/**
 * Apply explicit iframe sizing. Iframes ignore height:auto, so use a pixel floor
 * until HubSpot postMessage (or onFormReady) provides a real height.
 *
 * @param {HTMLElement} container
 * @param {number} [height]
 */
function strlNormalizeHubspotIframes( container, height ) {
	if ( ! container ) {
		return;
	}

	strlEnsureHubspotFormStyles( container );

	const targetHeight = height && height > 50 ? height : STRL_HS_MIN_HEIGHT;

	container.querySelectorAll( 'iframe' ).forEach( ( iframe ) => {
		iframe.removeAttribute( 'hidden' );
		iframe.style.setProperty( 'visibility', 'visible', 'important' );
		iframe.style.setProperty( 'opacity', '1', 'important' );
		iframe.style.setProperty( 'display', 'block', 'important' );
		iframe.style.setProperty( 'width', '100%', 'important' );
		iframe.style.setProperty( 'min-height', STRL_HS_MIN_HEIGHT + 'px', 'important' );

		const current = parseInt( iframe.style.height || iframe.getAttribute( 'height' ) || '0', 10 );
		if ( ! current || current < 50 || height ) {
			iframe.style.setProperty( 'height', Math.max( current, targetHeight ) + 'px', 'important' );
			iframe.setAttribute( 'height', String( Math.max( current, targetHeight ) ) );
		}
	} );

	container.querySelectorAll( '.hbspt-form' ).forEach( ( formWrap ) => {
		formWrap.style.setProperty( 'visibility', 'visible', 'important' );
		formWrap.style.setProperty( 'display', 'block', 'important' );
		formWrap.style.setProperty( 'min-height', STRL_HS_MIN_HEIGHT + 'px', 'important' );
	} );
}

/**
 * Patch hbspt.forms.create so onFormReady always normalizes iframe visibility/height.
 */
function strlPatchHubspotFormsCreate() {
	if ( ! window.hbspt || ! window.hbspt.forms || typeof window.hbspt.forms.create !== 'function' ) {
		return;
	}

	if ( window.hbspt.forms.create.__strlEventRegistrationPatched ) {
		return;
	}

	const originalCreate = window.hbspt.forms.create.bind( window.hbspt.forms );

	window.hbspt.forms.create = function ( config ) {
		const options = config && 'object' === typeof config ? Object.assign( {}, config ) : {};
		const userOnFormReady = options.onFormReady;

		options.onFormReady = function ( $form ) {
			document.querySelectorAll( '[data-event-registration-script-content]' ).forEach( ( container ) => {
				strlNormalizeHubspotIframes( container );
			} );

			window.setTimeout( () => {
				document.querySelectorAll( '[data-event-registration-script-content]' ).forEach( ( container ) => {
					strlNormalizeHubspotIframes( container );
				} );
			}, 100 );

			window.setTimeout( () => {
				document.querySelectorAll( '[data-event-registration-script-content]' ).forEach( ( container ) => {
					strlNormalizeHubspotIframes( container );
				} );
			}, 500 );

			if ( 'function' === typeof userOnFormReady ) {
				userOnFormReady( $form );
			}
		};

		return originalCreate( options );
	};

	window.hbspt.forms.create.__strlEventRegistrationPatched = true;
}

/**
 * Sequentially replace script tags so external HubSpot embed loads before inline create().
 *
 * @param {HTMLElement} container
 * @returns {Promise<void>}
 */
function strlReexecuteEventRegistrationScripts( container ) {
	const scripts = Array.from( container.querySelectorAll( 'script' ) );

	return scripts.reduce( ( chain, oldScript ) => {
		return chain.then( () => {
			return new Promise( ( resolve ) => {
				const newScript = strlCloneExecutableEventRegistrationScript( oldScript );
				const hasSrc = Boolean( newScript.src );
				const isHsForms = hasSrc && -1 !== newScript.src.indexOf( 'hsforms' );

				if ( hasSrc ) {
					if ( window.hbspt && window.hbspt.forms && isHsForms ) {
						strlPatchHubspotFormsCreate();
						oldScript.parentNode.replaceChild( newScript, oldScript );
						resolve();
						return;
					}

					newScript.onload = () => {
						if ( isHsForms ) {
							strlPatchHubspotFormsCreate();
						}
						resolve();
					};
					newScript.onerror = () => {
						resolve();
					};
				} else {
					strlPatchHubspotFormsCreate();
				}

				oldScript.parentNode.replaceChild( newScript, oldScript );

				if ( ! hasSrc ) {
					resolve();
				}
			} );
		} );
	}, Promise.resolve() );
}

/**
 * @param {HTMLElement} body
 * @returns {boolean}
 */
function strlIsEventRegistrationModalOpen( body ) {
	const modal = body.closest( '[data-contact-modal]' );
	return Boolean( modal && ! modal.hasAttribute( 'hidden' ) );
}

/**
 * Watch for HubSpot iframe insertion and keep normalizing until dimensions settle.
 *
 * @param {HTMLElement} body
 */
function strlWatchHubspotForm( body ) {
	const container = body.querySelector( '[data-event-registration-script-content]' );
	if ( ! container ) {
		return;
	}

	strlNormalizeHubspotIframes( container );

	if ( container._strlHsObserver ) {
		container._strlHsObserver.disconnect();
	}

	const observer = new MutationObserver( () => {
		strlNormalizeHubspotIframes( container );
	} );
	container._strlHsObserver = observer;
	observer.observe( container, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: [ 'style', 'height', 'visibility', 'hidden' ],
	} );

	[ 0, 100, 300, 600, 1200, 2000, 3500 ].forEach( ( delay ) => {
		window.setTimeout( () => {
			strlNormalizeHubspotIframes( container );
		}, delay );
	} );

	window.setTimeout( () => {
		observer.disconnect();
		container._strlHsObserver = null;
		strlNormalizeHubspotIframes( container );
	}, 6000 );
}

/**
 * Mount HubSpot (or other) registration markup from the inert template.
 *
 * @param {HTMLElement} body
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<void>}
 */
function strlMountEventRegistrationScript( body, options ) {
	const force = Boolean( options && options.force );
	const container = body.querySelector( '[data-event-registration-script-content]' );
	const template = body.querySelector( '[data-event-registration-script-template]' );

	if ( ! container || ! template ) {
		return Promise.resolve();
	}

	if ( ! force && 'true' === container.dataset.scriptsLoaded ) {
		container.removeAttribute( 'hidden' );
		strlWatchHubspotForm( body );
		return Promise.resolve();
	}

	container.replaceChildren();
	container.appendChild( template.content.cloneNode( true ) );
	container.removeAttribute( 'hidden' );
	container.dataset.scriptsLoaded = 'false';
	strlEnsureHubspotFormStyles( container );

	return strlReexecuteEventRegistrationScripts( container ).then( () => {
		container.dataset.scriptsLoaded = 'true';
		strlWatchHubspotForm( body );
	} );
}

/**
 * Mount HubSpot registration scripts when the registration modal is open.
 *
 * @param {{ forceRemount?: boolean }} [options]
 */
function strlSyncEventRegistrationScripts( options ) {
	const forceRemount = Boolean( options && options.forceRemount );
	const containers = document.querySelectorAll( '[data-event-registration-script-content]' );

	if ( ! containers.length ) {
		return;
	}

	containers.forEach( ( container ) => {
		const body = container.parentElement;

		if ( ! body ) {
			return;
		}

		// Never mount into a closed modal — HubSpot iframes end up with height:0 / visibility:hidden.
		if ( ! strlIsEventRegistrationModalOpen( body ) ) {
			return;
		}

		strlMountEventRegistrationScript( body, { force: forceRemount } );
	} );
}

/**
 * Apply HubSpot postMessage heights to registration modal iframes.
 */
function strlBindHubspotHeightMessages() {
	if ( window.__strlEventRegistrationHsMessagesBound__ ) {
		return;
	}
	window.__strlEventRegistrationHsMessagesBound__ = true;

	window.addEventListener( 'message', ( event ) => {
		const data = event.data;
		if ( ! data ) {
			return;
		}

		let height = 0;

		if ( 'object' === typeof data ) {
			if ( data.height ) {
				height = parseInt( data.height, 10 );
			} else if ( data.data && data.data.height ) {
				height = parseInt( data.data.height, 10 );
			}
		} else if ( 'string' === typeof data && -1 !== data.indexOf( 'height' ) ) {
			try {
				const parsed = JSON.parse( data );
				height = parseInt( parsed.height || ( parsed.data && parsed.data.height ) || '0', 10 );
			} catch ( error ) {
				height = 0;
			}
		}

		if ( ! height || height < 50 ) {
			return;
		}

		const origin = event.origin || '';
		const fromHubspot = -1 !== origin.indexOf( 'hubspot' ) || -1 !== origin.indexOf( 'hsforms' );
		const hasHsCallback = data && ( 'hsFormCallback' === data.type || data.formGuid || data.id );
		if ( ! fromHubspot && ! hasHsCallback ) {
			return;
		}

		document.querySelectorAll( '[data-event-registration-script-content]' ).forEach( ( container ) => {
			strlNormalizeHubspotIframes( container, height );
		} );
	} );
}

/**
 * Mount HubSpot when a registration modal is opened — always, even if another
 * block already claimed the shared contact-modal open/close handlers via
 * __strlContactsModalsReady__ (e.g. contact-form on the same page).
 */
function strlBindEventRegistrationMountOnOpen() {
	if ( window.__strlEventRegistrationMountBound__ ) {
		return;
	}
	window.__strlEventRegistrationMountBound__ = true;

	document.addEventListener( 'click', ( event ) => {
		const renewTrigger = event.target.closest( '[data-cookiebot-renew]' );
		if ( renewTrigger ) {
			event.preventDefault();
			if ( window.Cookiebot && typeof window.Cookiebot.renew === 'function' ) {
				window.Cookiebot.renew();
			}
			return;
		}

		const trigger = event.target.closest( '[data-contact-modal-target]' );
		if ( ! trigger ) {
			return;
		}

		const modalId = trigger.getAttribute( 'data-contact-modal-target' );
		const modal = modalId ? document.getElementById( modalId ) : null;
		if ( ! modal || ! modal.querySelector( '[data-event-registration-script-content]' ) ) {
			return;
		}

		// Mount after the modal is painted (another block may open it on this click).
		window.requestAnimationFrame( () => {
			window.requestAnimationFrame( () => {
				strlSyncEventRegistrationScripts( { forceRemount: true } );
			} );
		} );
	} );
}

function strlInitEventRegistrationModals() {
	strlBindHubspotHeightMessages();
	strlBindEventRegistrationMountOnOpen();

	if ( window.__strlContactsModalsReady__ ) {
		return;
	}
	window.__strlContactsModalsReady__ = true;

	let activeModal = null;

	const openModal = ( modalId ) => {
		if ( ! modalId ) {
			return;
		}

		const modal = document.getElementById( modalId );
		if ( ! modal || ! modal.matches( '[data-contact-modal]' ) ) {
			return;
		}

		modal.removeAttribute( 'hidden' );
		modal.setAttribute( 'aria-hidden', 'false' );
		document.documentElement.classList.add( 'overflow-hidden' );
		document.body.classList.add( 'overflow-hidden' );
		activeModal = modal;
	};

	const closeModal = ( modal ) => {
		if ( ! modal ) {
			return;
		}

		modal.setAttribute( 'aria-hidden', 'true' );
		modal.setAttribute( 'hidden', '' );
		document.documentElement.classList.remove( 'overflow-hidden' );
		document.body.classList.remove( 'overflow-hidden' );
		if ( activeModal === modal ) {
			activeModal = null;
		}
	};

	document.addEventListener( 'click', ( event ) => {
		const trigger = event.target.closest( '[data-contact-modal-target]' );
		if ( trigger ) {
			event.preventDefault();
			openModal( trigger.getAttribute( 'data-contact-modal-target' ) );
			return;
		}

		const closeTrigger = event.target.closest( '[data-contact-modal-close]' );
		if ( closeTrigger ) {
			event.preventDefault();
			closeModal( closeTrigger.closest( '[data-contact-modal]' ) );
			return;
		}

		const modal = event.target.closest( '[data-contact-modal]' );
		if ( modal && ! event.target.closest( '[data-contact-modal-panel]' ) ) {
			closeModal( modal );
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key && activeModal ) {
			closeModal( activeModal );
		}
	} );
}

strlInitEventRegistrationModals();
